/**
 * Maps API activity event_type values to display copy and icon styling.
 * @param {string} [eventType]
 */
export function getActivityEventDisplay(eventType) {
  const key = String(eventType || "")
    .toLowerCase()
    .trim();

  const map = {
    invite_sent: {
      label: "Invitation sent",
      description: "Email with sign-in link was sent",
      icon: "bi-envelope-check",
      iconClass: "text-primary",
    },
    otp_requested: {
      label: "Verification code requested",
      description: "A one-time code was sent to the vendor email",
      icon: "bi-shield-lock",
      iconClass: "text-info",
    },
    vendor_login: {
      label: "Vendor signed in",
      description: "Signed in with email and one-time code",
      icon: "bi-person-check",
      iconClass: "text-success",
    },
    response_saved: {
      label: "Draft saved",
      description: "Disclosure answers were saved",
      icon: "bi-save",
      iconClass: "text-secondary",
    },
    response_submitted: {
      label: "Response submitted",
      description: "Disclosure was marked as submitted",
      icon: "bi-send-check",
      iconClass: "text-success",
    },
    calculation_success: {
      label: "Emissions calculated",
      description: "Calculation completed successfully",
      icon: "bi-calculator",
      iconClass: "text-success",
    },
    calculation_failed: {
      label: "Calculation failed",
      description: "Emissions calculation returned an error",
      icon: "bi-exclamation-octagon",
      iconClass: "text-danger",
    },
  };

  if (map[key]) {
    return { key, ...map[key] };
  }

  return {
    key,
    label: eventType ? String(eventType).replace(/_/g, " ") : "Event",
    description: "",
    icon: "bi-dot",
    iconClass: "text-muted",
  };
}

/**
 * @param {string} [actorType]
 */
export function getActivityActorDisplay(actorType) {
  const key = String(actorType || "").toLowerCase().trim();
  const map = {
    admin: { label: "Company admin", badgeClass: "bg-primary" },
    vendor: { label: "Vendor", badgeClass: "bg-secondary" },
    system: { label: "System", badgeClass: "bg-dark" },
  };
  return map[key] || { label: actorType || "—", badgeClass: "bg-light text-dark border" };
}

/** Short names for Scope 3 category codes stored on assignments */
const SCOPE3_CATEGORY_LABELS = {
  1: "Purchased goods & services",
  4: "Upstream transport & distribution",
  5: "Waste generated in operations",
  9: "Downstream transport & distribution",
  10: "Processing of sold products",
};

/**
 * Builds human-readable detail lines from API `metaJson` (stored per activity row).
 * Older rows may only have `assignmentId` until new events are logged.
 * @param {object|null|undefined} meta
 * @param {string} [eventType]
 * @returns {string[]}
 */
export function formatActivityMetaDetail(meta, eventType) {
  if (!meta || typeof meta !== "object") return [];
  const lines = [];
  const m = meta;

  if (m.reportingYear) {
    lines.push(`Reporting year: ${m.reportingYear}`);
  }
  if (m.scope3Category != null && m.scope3Category !== "") {
    const cat = m.scope3Category;
    const name = SCOPE3_CATEGORY_LABELS[Number(cat)];
    lines.push(name ? `Scope 3 category ${cat} — ${name}` : `Scope 3 category ${cat}`);
  }
  if (m.assignmentId != null) {
    lines.push(`Assignment #${m.assignmentId}`);
  }
  if (m.calculationRunId != null) {
    lines.push(`Calculation run #${m.calculationRunId}`);
  }
  if (m.co2e != null && m.co2e !== "") {
    const unit = m.co2eUnit ? ` ${m.co2eUnit}` : "";
    lines.push(`Estimated CO₂e: ${m.co2e}${unit}`);
  }
  if (m.inviteId != null) {
    lines.push(`Invite #${m.inviteId}`);
  }
  if (m.emailSkipped === true || m.skippedEmail === true) {
    lines.push("Note: invitation email was not sent (SMTP not configured or dev mode).");
  }
  if (m.method === "otp") {
    lines.push("Sign-in method: one-time code");
  }
  if (m.errorMessage) {
    lines.push(`Error: ${m.errorMessage}`);
  }

  return lines;
}

/**
 * @param {string|Date} value
 */
export function formatActivityTimestamp(value) {
  try {
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return String(value);
  }
}
